import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Brain, CheckCircle2, Clock, ChevronRight, ChevronLeft, AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TakeAssessment() {
  const { accessCode } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentQ, setCurrentQ] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const fetchAssessment = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assessments/take/${accessCode}`);
      setAssessment(res.data);
      setTimeLeft(res.data.time_limit_minutes * 60);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar evaluación");
    } finally {
      setLoading(false);
    }
  }, [accessCode]);

  useEffect(() => { fetchAssessment(); }, [fetchAssessment]);

  // Timer
  useEffect(() => {
    if (!timeLeft || timeLeft <= 0 || completed) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timer); handleSubmit(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, completed]);

  const handleSelect = (questionId, selectedIndex) => {
    setResponses(prev => ({ ...prev, [questionId]: selectedIndex }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const formattedResponses = Object.entries(responses).map(([qId, idx]) => ({
        question_id: qId, selected_index: idx
      }));
      const res = await axios.post(`${API}/assessments/submit/${accessCode}`, { responses: formattedResponses });
      setCompleted(true);
      setScore(res.data.score);
      toast.success("Evaluación completada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="assessment-completed">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Evaluación Completada</h2>
            <p className="text-slate-500 mb-4">Gracias por completar la evaluación</p>
            {score !== null && (
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-4xl font-bold text-emerald-600">{score}%</p>
                <p className="text-sm text-emerald-700">Score obtenido</p>
              </div>
            )}
            <p className="text-sm text-slate-400 mt-4">Los resultados serán analizados por el equipo de RH</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!assessment) return null;

  const questions = assessment.questions || [];
  const totalQ = questions.length;
  const currentQuestion = questions[currentQ];
  const answeredCount = Object.keys(responses).length;
  const progressPct = totalQ > 0 ? Math.round((answeredCount / totalQ) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="take-assessment-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Brain className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-slate-900">{assessment.assessment_name}</p>
              <p className="text-xs text-slate-500">{assessment.candidate_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline">{answeredCount}/{totalQ}</Badge>
            {timeLeft !== null && (
              <div className={`flex items-center gap-1 text-sm font-mono ${timeLeft < 300 ? "text-red-500" : "text-slate-600"}`}>
                <Clock className="w-4 h-4" /> {formatTime(timeLeft)}
              </div>
            )}
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-2">
          <Progress value={progressPct} className="h-1.5" />
        </div>
      </div>

      {/* Question */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {currentQuestion && (
          <Card className="mb-6" data-testid={`question-${currentQuestion.id}`}>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <Badge variant="outline" className="text-xs">Pregunta {currentQ + 1} de {totalQ}</Badge>
                {currentQuestion.dimension && (
                  <Badge className="bg-violet-100 text-violet-700 text-xs">{currentQuestion.dimension.replace(/_/g, " ")}</Badge>
                )}
              </div>
              <CardTitle className="text-lg">{currentQuestion.text}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(currentQuestion.options || []).map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelect(currentQuestion.id, idx)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    responses[currentQuestion.id] === idx
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid={`option-${currentQuestion.id}-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      responses[currentQuestion.id] === idx ? "border-emerald-500 bg-emerald-500" : "border-slate-300"
                    }`}>
                      {responses[currentQuestion.id] === idx && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-sm">{opt}</span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>
          <div className="flex gap-1">
            {questions.map((_, i) => (
              <button key={i} onClick={() => setCurrentQ(i)}
                className={`w-3 h-3 rounded-full transition-all ${
                  i === currentQ ? "bg-emerald-500 scale-125" : responses[questions[i]?.id] !== undefined ? "bg-emerald-300" : "bg-slate-300"
                }`} />
            ))}
          </div>
          {currentQ < totalQ - 1 ? (
            <Button onClick={() => setCurrentQ(currentQ + 1)}>
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting || answeredCount === 0} data-testid="submit-assessment-btn">
              {submitting ? "Enviando..." : "Finalizar Evaluación"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
